import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PaymentMethodType } from "@/core";
import { useBuyBackPurchaseOrder } from "@/hooks";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { formatCurrency, formatNumber } from "./operations-shared";

const getToday = () => new Date().toISOString().split("T")[0];

interface BuyBackPurchaseOrderFormProps {
  poId: string;
  amountBought: number;
  alreadyReturned: number;
  alreadyRefunded: number;
  clearedOutgoing: number;
  inventoryAvailableMiles?: number;
  defaultMethod?: PaymentMethodType;
  onSuccess?: () => void;
}

export function BuyBackPurchaseOrderForm(
  props: Readonly<BuyBackPurchaseOrderFormProps>,
) {
  const {
    poId,
    amountBought,
    alreadyReturned,
    alreadyRefunded,
    clearedOutgoing,
    inventoryAvailableMiles,
    defaultMethod,
    onSuccess,
  } = props;
  const buyBackMutation = useBuyBackPurchaseOrder();

  const poRemainingMiles = Math.max(amountBought - alreadyReturned, 0);
  // Inventory availability is the hard ceiling — miles already sold to clients
  // can't be returned. If availableMiles < poRemainingMiles, use that lower cap.
  const remainingMiles =
    inventoryAvailableMiles !== undefined
      ? Math.min(poRemainingMiles, Math.max(inventoryAvailableMiles, 0))
      : poRemainingMiles;
  const inventoryConstrained =
    inventoryAvailableMiles !== undefined &&
    inventoryAvailableMiles < poRemainingMiles;

  const [milesReturned, setMilesReturned] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [method, setMethod] = useState<PaymentMethodType>(
    defaultMethod ?? "bank",
  );
  const [date, setDate] = useState(getToday);
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");

  const milesNum = Number(milesReturned);
  const refundNum = Number(refundAmount);
  const milesValid =
    Number.isFinite(milesNum) && milesNum > 0 && milesNum <= remainingMiles;
  const refundValid = Number.isFinite(refundNum) && refundNum >= 0;
  const willVoid = milesValid && milesNum === remainingMiles;
  const overRefund = refundValid && alreadyRefunded + refundNum > clearedOutgoing;

  const handleSubmit = async () => {
    if (!milesValid || !refundValid) {
      return;
    }
    try {
      await buyBackMutation.mutateAsync({
        poId,
        milesReturned: milesNum,
        refundAmount: refundNum,
        method,
        paidAt: date,
        referenceId: reference.trim() || undefined,
        reason: reason.trim() || undefined,
      });
    } catch {
      return;
    }
    setMilesReturned("");
    setRefundAmount("");
    setReference("");
    setReason("");
    setDate(getToday());
    onSuccess?.();
  };

  // With no returnable miles the form can never validate, so say why rather than
  // showing a permanently dead submit button.
  if (remainingMiles <= 0) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
        <span>
          {poRemainingMiles <= 0
            ? "Every mile on this purchase order has already been returned, so there is nothing left to buy back."
            : `The linked inventory has no miles available, so this PO's ${formatNumber({ value: poRemainingMiles })} remaining miles can't be returned to the vendor — they have already been sold to clients. Unwind the sales orders that consumed them first, or cancel the PO if no money was ever paid.`}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">
        Returns miles to the vendor and records the money coming back. Up to{" "}
        {formatNumber({ value: remainingMiles })} miles can be bought back
        {alreadyReturned > 0
          ? ` (${formatNumber({ value: alreadyReturned })} already returned)`
          : ""}
        {inventoryConstrained
          ? ` — limited by inventory availability (${formatNumber({ value: inventoryAvailableMiles })} available; ${formatNumber({ value: poRemainingMiles - remainingMiles })} already sold to clients).`
          : "."}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>Miles Returned</Label>
          <Input
            type="number"
            min="0"
            max={remainingMiles}
            step="1"
            placeholder="0"
            value={milesReturned}
            onChange={(e) => setMilesReturned(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label>Refund Amount</Label>
          <Input
            type="number"
            min="0"
            placeholder="0.00"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
          />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>Method</Label>
          <Select
            value={method}
            onValueChange={(v) => setMethod(v as PaymentMethodType)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="paypal_goods_services">
                PayPal - Goods and Services
              </SelectItem>
              <SelectItem value="paypal_friends_family">
                PayPal - Friends and Family
              </SelectItem>
              <SelectItem value="paypal_goods_services_fees">
                PayPal - Goods and Services + Fees
              </SelectItem>
              <SelectItem value="bank">Bank</SelectItem>
              <SelectItem value="zelle">Zelle</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="crypto">Crypto</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Date</Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label>Reference (optional)</Label>
        <Input
          placeholder="Transaction ID, check number, etc."
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label>Reason (optional)</Label>
        <Textarea
          placeholder="Why is this being bought back?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
        />
      </div>
      {willVoid ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>
            This returns all remaining miles and will VOID the purchase order.
          </span>
        </div>
      ) : null}
      {overRefund ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Total refunds ({formatCurrency({ value: alreadyRefunded + refundNum })}
            ) exceed what was paid to the vendor (
            {formatCurrency({ value: clearedOutgoing })}). Continue only if this
            is intentional.
          </span>
        </div>
      ) : null}
      <Button
        variant="destructive"
        onClick={handleSubmit}
        disabled={!milesValid || !refundValid}
        loading={buyBackMutation.isPending}
        loadingText="Recording..."
      >
        {willVoid ? "Buy Back & Void PO" : "Record Buyback"}
      </Button>
    </div>
  );
}
